import React, { useEffect, useState } from "react";
import Select from "react-select";
import { supplierPart } from "../models/SupplierParts";
import "../styles/AddItemForm.css";
import { getData } from "../api/ApiServices";
import { ApiResponse, CurrencyResponse } from "../models/currencytypes";
import { fetchItems } from "../api/ItemService";
import { SupplierPartUtils } from "../utils/itemlist/supplierPartsUtils";
import { fetchDropDownData } from "../utils/fetchDropDownData";
import { log } from "console";

interface AddSupplierPartFormProps {
  onSave: (supplierPart: supplierPart) => Promise<void>;
  onCancel: () => void;
  suppliers: any[];
  isProcessing?: boolean;
  isEditing?: boolean;
  editingSupplierPart?: supplierPart | null;
}

// Update the interface to properly type the fields
interface FormSupplierPart
  extends Omit<supplierPart, "unitPrice" | "gst" | "sgst" | "cgst" | "igst"> {
  unitPrice: number | string;
  gst: number | string;
  sgst: number | string;
  cgst: number | string;
  igst: number | string;
  partName?: string;
}

const AddSupplierPartForm: React.FC<AddSupplierPartFormProps> = ({
  onSave,
  onCancel,
  suppliers,
  isProcessing,
  isEditing,
  editingSupplierPart,
}) => {
  const {
    supplierOptions,
    setSupplierOptions,
    fetchSupplierOptions,
    partNoOptions,
    setPartNoOptions,
    fetchPartOptions,
    gstOptions,
    setGstOptions,
    fetchGstOptions,
  } = SupplierPartUtils();

  const [supplierPart, setSupplierPart] = useState<FormSupplierPart>({
    supplierId: "",
    partId: "",
    currency: "",
    unitPrice: 0,
    gst: 0,
    sgst: 0,
    cgst: 0,
    igst: 0,
    status: "ACTIVE",
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  // const [supplierOptions, setSupplierOptions] = useState<{ value: string; label: string; currency?: string, purchaseCurrencyId?: string }[]>([]);
  const [currencyOptions, setCurrencyOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [CurrencyResponse, setCurrencyResponse] = useState<any>();
  const [currencyMap, setCurrencyMap] = useState<{ [key: string]: string }>({});

  const [items, setItems] = useState<any>();
  const [searchTerm, setSearchTerm] = useState("");

  // const [partNoOptions, setPartNoOptions] = useState<{ label: string; value: string; partNo: string, partName: string }[]>([]);
  const [gstSlabs, setGstSlabs] = useState<any[]>([]);
  // const [gstOptions, setGstOptions] = useState<{ label: string; value: number; sgst: number; cgst: number; igst: number }[]>([]);

  useEffect(() => {
    fetchSupplierOptions("");
    fetchPartOptions("");
    fetchGstOptions("");
  }, []);

  useEffect(() => {
    console.log(editingSupplierPart);

    if (suppliers.length > 0) {
      const options = suppliers.map((supplier) => ({
        value: supplier._id,
        label: supplier.name,
        purchaseCurrencyId: supplier.purchaseCurrencyId, // Store currency ID
      }));
      setSupplierOptions(options);
    }

    if (isEditing && editingSupplierPart) {
      setSupplierPart(editingSupplierPart as FormSupplierPart);
    } else {
      setSupplierPart({
        supplierId: "",
        partId: "",
        currency: "",
        unitPrice: 0,
        gst: 0,
        sgst: 0,
        cgst: 0,
        igst: 0,
        status: "ACTIVE",
      });
    }

    // Clear any previous errors when component mounts/remounts
    setErrors({});
    fetchCurrencies();
  }, [isEditing, editingSupplierPart, suppliers]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const numericFields = ["unitPrice", "gst", "sgst", "cgst", "igst"];

    if (name === "unitPrice") {
      // Allow empty value for clearing
      if (value === "") {
        setSupplierPart((prev) => ({ ...prev, [name]: "" }));
        setErrors((prev) => ({ ...prev, [name]: "" }));
        return;
      }

      // Allow up to 2 decimal places for unit price
      if (/^\d*\.?\d{0,2}$/.test(value)) {
        const parsedValue = Number(value);
        if (!isNaN(parsedValue) && parsedValue >= 0) {
          setSupplierPart((prev) => ({ ...prev, [name]: value }));
          setErrors((prev) => ({ ...prev, [name]: "" }));
        }
      }
    } else if (numericFields.includes(name)) {
      // Allow empty value to let the user erase the field
      if (value === "") {
        setSupplierPart((prev) => ({ ...prev, [name]: "" }));
        setErrors((prev) => ({ ...prev, [name]: "" }));
        return;
      }

      // Check if input is a valid number (prevents non-numeric input)
      if (/^\d*\.?\d*$/.test(value)) {
        const parsedValue = Number(value);

        // Ensure it's not negative
        if (!isNaN(parsedValue) && parsedValue >= 0) {
          // For tax fields, ensure they don't exceed 100%
          if (
            (name === "gst" ||
              name === "sgst" ||
              name === "cgst" ||
              name === "igst") &&
            parsedValue > 100
          ) {
            setErrors((prev) => ({ ...prev, [name]: "Cannot exceed 100%" }));
            return;
          }

          setSupplierPart((prev) => ({ ...prev, [name]: parsedValue }));
          setErrors((prev) => ({ ...prev, [name]: "" }));
        }
      }
    } else {
      // Allow normal text input for string fields
      setSupplierPart((prev) => ({ ...prev, [name]: value }));
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSelectChange = (selectedOption: any, { name }: any) => {
    setSupplierPart((prev) => ({
      ...prev,
      [name]: selectedOption?.value || "",
    }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    // Required fields validation
    if (!supplierPart.supplierId) {
      newErrors.supplierId = "Required";
    }

    if (!supplierPart.partId) {
      newErrors.partId = "Required";
    }

    if (!supplierPart.currency) {
      newErrors.currency = "Required";
    }

    // Numeric fields validation - checking for zero values or empty values
    if (supplierPart.unitPrice === 0 || supplierPart.unitPrice === "") {
      newErrors.unitPrice = "Required";
    }

    if (
      supplierPart.gst === "" ||
      supplierPart.gst === null ||
      supplierPart.gst === undefined
    ) {
      newErrors.gst = "Required";
    }

    // Additional validation for tax fields - non-negative and <= 100
    const taxFields = ["gst", "sgst", "cgst", "igst"];
    taxFields.forEach((field) => {
      const value = supplierPart[field as keyof FormSupplierPart];
      if (typeof value === "number" && value < 0) {
        newErrors[field] = "Cannot be negative";
      }

      if (typeof value === "number" && value > 100) {
        newErrors[field] = "Cannot exceed 100%";
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log("post data: ", supplierPart);

    if (validateForm()) {
      const formattedSupplierPart: supplierPart = {
        ...supplierPart,
        unitPrice:
          typeof supplierPart.unitPrice === "string"
            ? parseFloat(supplierPart.unitPrice) || 0
            : supplierPart.unitPrice,
        gst:
          typeof supplierPart.gst === "string"
            ? parseFloat(supplierPart.gst) || 0
            : supplierPart.gst,
        sgst:
          typeof supplierPart.sgst === "string"
            ? parseFloat(supplierPart.sgst) || 0
            : supplierPart.sgst,
        cgst:
          typeof supplierPart.cgst === "string"
            ? parseFloat(supplierPart.cgst) || 0
            : supplierPart.cgst,
        igst:
          typeof supplierPart.igst === "string"
            ? parseFloat(supplierPart.igst) || 0
            : supplierPart.igst,
      };

      onSave(formattedSupplierPart);
    }
  };

  const fetchCurrencies = async () => {
    try {
      const response = await getData<ApiResponse<CurrencyResponse>>(
        "/currencymaster",
        { page: 1, limit: 100 }
      );
      if (response?.data?.data && Array.isArray(response.data.data)) {
        const currencyMap: { [key: string]: string } = {};
        response.data.data.forEach((c) => {
          if (c._id) {
            // Ensure _id is defined
            currencyMap[c._id] = c.name ?? ""; // Store currency name by ID, fallback to empty string
          }
        });
        setCurrencyMap(currencyMap);
      }
    } catch (error) {
      console.error("Error fetching currencies:", error);
    }
  };

  useEffect(() => {
    setSupplierPart((prev) => ({
      ...prev,
      partName: editingSupplierPart?.partDetails?.partName,
    }));
  }, [editingSupplierPart]);

  const handleSupplierChange = (selectedOption: any) => {
    const selectedSupplier = suppliers.find(
      (s) => s._id === selectedOption?.value
    );
    const currencyName = selectedSupplier?.purchaseCurrencyId
      ? currencyMap[selectedSupplier.purchaseCurrencyId]
      : "";

    console.log(currencyName);

    setSupplierPart((prev) => ({
      ...prev,
      supplierId: selectedOption?.value || "",
      currency: currencyName,
    }));

    // Clear errors for these fields
    setErrors((prev) => ({
      ...prev,
      supplierId: "",
      currency: "",
    }));

    console.log(supplierPart);
  };

  const handlePartNoChange = (selectedOption: any) => {
    if (selectedOption) {
      setSupplierPart({
        ...supplierPart,
        partId: selectedOption.value, // Store partId
        partName: selectedOption.partName, // Auto-update partName
      });
      setErrors((prev) => ({ ...prev, partId: "" }));
    }
  };

  // const loadGstSlabs = async () => {
  //     try {
  //         const response = await getData<ApiResponse<any>>("/gstslabs", {
  //             page: 1,
  //             limit: 100,
  //             filter: {
  //                 status: "ACTIVE"
  //             }
  //         });

  //         console.log("Fetched GST Slabs:", response);
  //         setGstSlabs(response?.data?.data || []);

  //         // Create dropdown options
  //         const options = response?.data?.data?.map((gst: any) => ({
  //             label: gst.gstPercentage,
  //             value: gst.gstPercentage,
  //             sgst: gst.sgst,
  //             cgst: gst.cgst,
  //             igst: gst.igst,
  //         })) || [];

  //         setGstOptions(options);
  //     } catch (error) {
  //         console.error("Failed to fetch GST slabs:", error);
  //     }
  // };

  const handleGstChange = (selectedOption: any) => {
    const selectedGst = gstOptions.find(
      (g) => g.value === selectedOption?.value
    );

    setSupplierPart((prev) => ({
      ...prev,
      gst: selectedOption?.value || "",
      sgst: selectedGst?.sgst || 0,
      cgst: selectedGst?.cgst || 0,
      igst: selectedGst?.igst || 0,
    }));

    setErrors((prev) => ({
      ...prev,
      gst: "",
      sgst: "",
      cgst: "",
      igst: "",
    }));
  };

  const statusOptions = [
    { value: "ACTIVE", label: "ACTIVE" },
    { value: "INACTIVE", label: "INACTIVE" },
  ];

  return (
    <>
      <div className="breadcrumb">
        <span className="breadcrumb-item" onClick={onCancel}>
          Supplier Part
        </span>
        <span className="breadcrumb-separator"> &gt; </span>
        <span className="breadcrumb-current">
          {isEditing ? "Edit Supplier Part" : "Add Supplier Part"}
        </span>
      </div>

      <div className="form-container">
        <form onSubmit={handleSubmit} className="add-item-form" noValidate>
          <div className="form-grid">
            <div className="form-group">
              <label>
                Supplier Code:<span className="required">*</span>
              </label>
              <Select
                name="supplierId"
                options={supplierOptions}
                value={supplierOptions.find(
                  (o) => o.value === supplierPart.supplierId
                )}
                onChange={handleSupplierChange}
                onInputChange={(inputValue) => {
                  setSearchTerm(inputValue);
                  fetchSupplierOptions(inputValue);
                }}
                placeholder="Select Supplier"
                className={`react-select-container ${
                  errors.supplierId ? "error-select" : ""
                }`}
                classNamePrefix="react-select"
              />
              {errors.supplierId && (
                <span className="error-message">{errors.supplierId}</span>
              )}
            </div>
            <div className="form-group">
              <label>
                Currency :<span className="required">*</span>
              </label>
              <input
                type="text"
                name="currency"
                value={supplierPart.currency}
                readOnly
                placeholder="Currency"
                className={`form-control read-only-input ${
                  errors.currency ? "error-input" : ""
                }`}
              />
              {errors.currency && (
                <span className="error-message">{errors.currency}</span>
              )}
            </div>
            <div className="form-group">
              <label>
                Part No :<span className="required">*</span>
              </label>
              <Select
                name="partId"
                options={partNoOptions}
                value={partNoOptions.find(
                  (o) => o.value === supplierPart.partId
                )}
                onChange={handlePartNoChange}
                onInputChange={(inputValue) => {
                  setSearchTerm(inputValue);
                  fetchPartOptions(inputValue);
                }}
                placeholder="Select Part No"
                className={`react-select-container ${
                  errors.partId ? "error-select" : ""
                }`}
                classNamePrefix="react-select"
              />
              {errors.partId && (
                <span className="error-message">{errors.partId}</span>
              )}
            </div>

            <div className="form-group">
              <label>Part Name :</label>
              <input
                type="text"
                value={supplierPart.partName}
                readOnly
                className="form-control read-only-input"
                placeholder="Selected Part Name"
              />
            </div>

            <div className="form-group">
              <label>
                Unit Price :<span className="required">*</span>
              </label>
              <input
                type="text"
                name="unitPrice"
                value={supplierPart.unitPrice}
                onChange={handleChange}
                className={errors.unitPrice ? "error-input" : ""}
                placeholder="0.00"
              />

              {errors.unitPrice && (
                <span className="error-message">{errors.unitPrice}</span>
              )}
            </div>
            <div className="form-group">
              <label>
                GST :<span className="required">*</span>
              </label>
              <Select
                name="gst"
                options={gstOptions}
                value={gstOptions.find((o) => o.value === supplierPart.gst)}
                onChange={handleGstChange}
                onInputChange={(inputValue) => {
                  setSearchTerm(inputValue);
                  fetchGstOptions(inputValue);
                }}
                placeholder="Select GST"
                className={`react-select-container ${
                  errors.gst ? "error-select" : ""
                }`}
                classNamePrefix="react-select"
              />
              {errors.gst && (
                <span className="error-message">{errors.gst}</span>
              )}
            </div>

            <div className="form-group">
              <label>SGST :</label>
              <input
                type="text"
                name="sgst"
                value={supplierPart.sgst}
                readOnly
                className={`form-control read-only-input ${
                  errors.sgst ? "error-input" : ""
                }`}
              />
              {errors.sgst && (
                <span className="error-message">{errors.sgst}</span>
              )}
            </div>

            <div className="form-group">
              <label>CGST :</label>
              <input
                type="text"
                name="cgst"
                value={supplierPart.cgst}
                readOnly
                className={`form-control read-only-input ${
                  errors.cgst ? "error-input" : ""
                }`}
              />
              {errors.cgst && (
                <span className="error-message">{errors.cgst}</span>
              )}
            </div>

            <div className="form-group">
              <label>IGST :</label>
              <input
                type="text"
                name="igst"
                value={supplierPart.igst}
                readOnly
                className={`form-control read-only-input ${
                  errors.igst ? "error-input" : ""
                }`}
              />
              {errors.igst && (
                <span className="error-message">{errors.igst}</span>
              )}
            </div>

            {isEditing && (
              <div className="form-group">
                <label>
                  Status :<span className="required">*</span>
                </label>
                <Select
                  name="status"
                  options={statusOptions}
                  value={statusOptions.find(
                    (o) => o.value === supplierPart.status
                  )}
                  onChange={handleSelectChange}
                  className={`react-select-container ${
                    errors.status ? "error-select" : ""
                  }`}
                  classNamePrefix="react-select"
                />
                {errors.status && (
                  <span className="error-message">{errors.status}</span>
                )}
              </div>
            )}
          </div>

          <div className="button-container">
            <button
              type="button"
              className="button cancel-button"
              onClick={onCancel}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="button save-button"
              disabled={isProcessing}
            >
              {isProcessing
                ? "Saving..."
                : isEditing
                ? "Update Supplier Part"
                : "Add Supplier Part"}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default AddSupplierPartForm;
